///
/// EarthBrowser
///
/// Legend tray: the one place layer legends live. Shows a legend for
/// every visible layer that declares one in layerRegistry, in registry
/// order, each as a full card or a collapsed chip. Enabling a layer opens
/// its legend and collapses the one the tray opened before, so the tray
/// never stacks cards on its own; cards the user opened stay open.
/// Per-legend state persists in userInterface.legends.
///
/// The tray as a whole hides behind one toggle button that never moves
/// (dialog.legendTrayHidden). Hiding is for this session: a reload or
/// enabling a layer that has a legend brings the tray back, so there is
/// no way to lose the legends.
///
/// Desktop: bottom-left on the same baseline as the time control and
/// camera stack. It never moves for the Layers panel: the tray publishes
/// its height (cLegendTrayLiftVar) and the panel ends above it, with the
/// card column capped shorter while the panel is open so both keep a
/// usable height. The toggle and chips wrap within the card column from
/// the bottom up, so they stack in rows instead of running under the
/// time bar, which keeps its left edge clear of the column.
/// Phones: chips only, riding above the full-width time control, level
/// with the camera stack; tapping a chip opens the legend in LegendSheet.
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import { LAYERS, type LayerDefinition } from "@/components/controls/layers-panel/layerRegistry"
import { useIsMobile } from "@/components/utils/use-mobile"
import { cLayersPanelEdgeInset, cLegendTrayLiftVar, cLegendTrayWidth, cTimeControlHeightVar, useChromeInsets } from "@/hooks/useBottomChromeOffset"
import { loadStateLabel, useLayerLoadStates, type DisplayedLoadState } from "@/hooks/useLayerLoadStates"
import { setLegendSheet, setLegendTrayHidden } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { legendLayerEnabled, setLegendDisplay, type LegendDisplayState } from "@/redux/userInterfaceSlice"
import type { DatasetType } from "@/types/DatasetElement"
import { cn } from "@/utils/utils"
import { useEffect, useMemo, useRef } from "react"
import { LegendCard, LegendChip, LegendTrayToggle } from "./LegendCard"
import { LegendSheet } from "./LegendSheet"

/** Layers with a legend, in registry order; `visibility` is set for all of them */
const cLegendLayers = LAYERS.filter(layer => layer.legend && layer.visibility)

// The phone camera stack's footprint (36px + 8px inset + gap) the chip row
// must leave free at its right
const cPhoneCameraStackWidth = 60

// Gap between the tray's top and the Layers panel's bottom
const cTrayPanelGap = 8

export function LegendTray() {
	const dispatch = useAppDispatch()
	const isMobile = useIsMobile()
	const insets = useChromeInsets()
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const display = useAppSelector(state => state.userInterface.legends?.display)
	const trayHidden = useAppSelector(state => state.dialog.legendTrayHidden)
	const infoSheetOpen = useAppSelector(state => state.dialog.mobileInfoSheetOpen)
	const layersPanelOpen = useAppSelector(state => state.dialog.layersPanelOpen)
	const timeZoneMode = useAppSelector(state => state.earthbrowser.timeZoneMode)

	// Loading rings on the chips/cards: the one surface beside the globe
	// that says "data still coming" while the Layers panel is closed. Only
	// layers on the globe count; a hidden sub-dataset syncing in the
	// background (quake history, slab geometry) doesn't change the picture
	const loadStates = useLayerLoadStates()
	const loadStateOf = (layer: LayerDefinition): DisplayedLoadState | null =>
		loadStates.find(state => state.visible && state.type === layer.visibility) ?? null

	const activeLayers = useMemo(
		() => cLegendLayers.filter(layer => visibleDatasets.includes(layer.visibility!)),
		[visibleDatasets]
	)

	// A layer with a legend just became visible: let the tray open it, and
	// bring the tray back if it was hidden. The first pass only records
	// what is visible so a reload doesn't re-open every legend
	const seenRef = useRef<Set<DatasetType> | null>(null)
	useEffect(() => {
		const current = new Set(activeLayers.map(layer => layer.visibility!))
		const seen = seenRef.current
		if (seen) {
			let enabled = false
			for (const type of current) {
				if (!seen.has(type)) {
					dispatch(legendLayerEnabled(type))
					enabled = true
				}
			}
			if (enabled) dispatch(setLegendTrayHidden(false))
		}
		seenRef.current = current
	}, [activeLayers, dispatch])

	// Publish the rendered height so the desktop Layers panel can end
	// above the tray. Observed rather than computed: cards and wrapped
	// chip rows change it. 0 (not just the gap) while nothing is shown
	const rootRef = useRef<HTMLDivElement>(null)
	const showsTray = activeLayers.length > 0 && !isMobile
	useEffect(() => {
		const root = document.documentElement
		const el = rootRef.current
		if (!showsTray || !el) {
			root.style.setProperty(cLegendTrayLiftVar, "0px")
			return
		}
		const publish = () => {
			root.style.setProperty(cLegendTrayLiftVar, `${Math.round(el.getBoundingClientRect().height) + cTrayPanelGap}px`)
		}
		publish()
		const observer = new ResizeObserver(publish)
		observer.observe(el)
		return () => {
			observer.disconnect()
			root.style.setProperty(cLegendTrayLiftVar, "0px")
		}
	}, [showsTray])

	if (activeLayers.length === 0) {
		return null
	}

	const stateOf = (layer: LayerDefinition): LegendDisplayState => display?.[layer.visibility!] ?? "chip"
	// Phones have no cards: everything is a chip
	const expanded = isMobile || trayHidden ? [] : activeLayers.filter(layer => stateOf(layer) === "expanded")
	const chips = trayHidden ? [] : (isMobile ? activeLayers : activeLayers.filter(layer => stateOf(layer) === "chip"))

	const setDisplay = (layer: LayerDefinition, state: LegendDisplayState) =>
		dispatch(setLegendDisplay({ type: layer.visibility!, display: state }))
	const expand = (layer: LayerDefinition) =>
		isMobile ? dispatch(setLegendSheet(layer.visibility!)) : setDisplay(layer, "expanded")

	const left = cLayersPanelEdgeInset
	// Desktop chips wrap within the card column (w-56); phone chips wrap
	// within the width left of the camera stack
	const rowMaxWidth = isMobile
		? `calc(100vw - ${left + cLayersPanelEdgeInset + cPhoneCameraStackWidth}px)`
		: cLegendTrayWidth

	return (
		<>
			<div
				ref={rootRef}
				className={cn(
					"fixed z-30 flex flex-col items-start gap-2 pointer-events-none",
					"transition-opacity duration-300",
					// Phones: the focused-entity info sheet docks over the bottom
					// edge; fade away with the time control and camera stack
					// (invisible also stops the chips taking taps)
					infoSheetOpen && "opacity-0 invisible"
				)}
				style={{
					left,
					bottom: isMobile
						? `calc(${insets.bottom}px + var(${cTimeControlHeightVar}, 0px) + 8px)`
						: insets.bottom,
				}}
				role="region"
				aria-label="Layer legends"
				aria-hidden={infoSheetOpen || undefined}
			>
				{expanded.length > 0 && (
					// Bounded so user-opened cards can't climb off the top of a
					// short viewport; the one-open rule keeps it from scrolling
					// in normal use. Shorter while the Layers panel shares the
					// column, so the panel keeps room for its list
					<div className={cn(
						"pointer-events-auto flex flex-col items-start gap-2 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent]",
						layersPanelOpen ? "max-h-[40dvh]" : "max-h-[calc(100dvh-160px)]"
					)}>
						{expanded.map(layer => {
							const Content = layer.legend!.content
							const loadState = loadStateOf(layer)
							return (
								<LegendCard
									key={layer.id}
									title={layer.name}
									icon={layer.icon}
									accent={layer.legend!.accent}
									loadState={loadState}
									loadingLabel={loadState ? loadStateLabel(loadState, timeZoneMode) : undefined}
									onCollapse={() => setDisplay(layer, "chip")}
								>
									<Content />
								</LegendCard>
							)
						})}
					</div>
				)}
				{/* wrap-reverse: the first line (toggle, then chips) sits at the
				    bottom and further lines stack above it, so the toggle is
				    always bottom-left */}
				<div className="flex flex-wrap-reverse items-center gap-1.5" style={{ maxWidth: rowMaxWidth }}>
					<LegendTrayToggle shown={!trayHidden} onToggle={() => dispatch(setLegendTrayHidden(!trayHidden))} />
					{chips.map(layer => {
						const loadState = loadStateOf(layer)
						return (
							<LegendChip
								key={layer.id}
								title={layer.name}
								label={layer.legend!.chipLabel}
								icon={layer.icon}
								accent={layer.legend!.accent}
								loadState={loadState}
								loadingLabel={loadState ? loadStateLabel(loadState, timeZoneMode) : undefined}
								onExpand={() => expand(layer)}
							/>
						)
					})}
				</div>
			</div>
			{isMobile && <LegendSheet />}
		</>
	)
}
